import React, { useState, useEffect } from 'react';
import './CameraManagement.css';

// クラウドカメラ管理コンポーネント
const CameraManagement = () => {
  const [cameraData, setCameraData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' または 'list'

  useEffect(() => {
    // データ取得関数
    const fetchCameraData = async () => {
      try {
        setLoading(true);
        
        // モックデータ
        const mockData = {
          cameras: [
            { 
              id: 1,
              name: '仙台本社エントランス', 
              location: '仙台市青葉区', 
              status: 'online', 
              lastUpdate: '2025-03-18T08:30:22',
              resolution: '1080p',
              storage: 85, // GB
              thumbnail: 'https://via.placeholder.com/320x180.png?text=仙台本社エントランス'
            },
            { 
              id: 2,
              name: '仙台工場A棟', 
              location: '仙台市宮城野区', 
              status: 'online', 
              lastUpdate: '2025-03-18T08:29:45',
              resolution: '1080p',
              storage: 92, // GB
              thumbnail: 'https://via.placeholder.com/320x180.png?text=仙台工場A棟'
            },
            { 
              id: 3,
              name: '仙台工場B棟', 
              location: '仙台市宮城野区', 
              status: 'offline', 
              lastUpdate: '2025-03-17T15:45:10',
              resolution: '720p',
              storage: 45, // GB
              thumbnail: 'https://via.placeholder.com/320x180.png?text=仙台工場B棟+オフライン'
            },
            { 
              id: 4,
              name: '石巻事業所', 
              location: '石巻市', 
              status: 'online', 
              lastUpdate: '2025-03-18T08:28:33',
              resolution: '1080p',
              storage: 78, // GB
              thumbnail: 'https://via.placeholder.com/320x180.png?text=石巻事業所'
            },
            { 
              id: 5,
              name: '気仙沼事業所', 
              location: '気仙沼市', 
              status: 'warning', 
              lastUpdate: '2025-03-18T08:15:19',
              resolution: '1080p',
              storage: 12, // GB
              thumbnail: 'https://via.placeholder.com/320x180.png?text=気仙沼事業所+警告'
            },
            { 
              id: 6,
              name: '名取倉庫', 
              location: '名取市', 
              status: 'online', 
              lastUpdate: '2025-03-18T08:31:05',
              resolution: '1080p',
              storage: 65, // GB
              thumbnail: 'https://via.placeholder.com/320x180.png?text=名取倉庫'
            }
          ],
          summary: {
            totalCameras: 6,
            onlineCount: 4,
            offlineCount: 1,
            warningCount: 1,
            totalStorage: 377, // GB
            usedStorage: 215, // GB
            updateDate: '2025年3月18日 08:35'
          }
        };
        
        // データ取得の遅延をシミュレート
        setTimeout(() => {
          setCameraData(mockData);
          setLoading(false);
        }, 1000);
        
      } catch (err) {
        console.error('カメラデータの取得中にエラーが発生しました:', err);
        setError('データの取得に失敗しました。後でもう一度お試しください。');
        setLoading(false);
      }
    };

    fetchCameraData();
  }, []);

  // ステータスに基づいたクラス名を返す関数
  const getStatusClass = (status) => {
    switch(status) {
      case 'online':
        return 'status-online';
      case 'offline':
        return 'status-offline';
      case 'warning':
        return 'status-warning';
      default:
        return '';
    }
  };

  // ステータスの日本語表示を返す関数
  const getStatusText = (status) => {
    switch(status) {
      case 'online':
        return 'オンライン';
      case 'offline':
        return 'オフライン';
      case 'warning':
        return '警告';
      default:
        return '不明';
    }
  };

  // 日時をフォーマットする関数
  const formatDateTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    return `${date.getMonth() + 1}/${date.getDate()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  // カメラをクリックした時のハンドラ
  const handleCameraClick = (camera) => {
    setSelectedCamera(camera);
  };

  // カメラ詳細を閉じるハンドラ
  const handleCloseDetail = () => {
    setSelectedCamera(null);
  };

  // 表示モードを切り替えるハンドラ
  const toggleViewMode = () => {
    setViewMode(viewMode === 'grid' ? 'list' : 'grid');
  };

  // ローディング中の表示
  if (loading) {
    return (
      <div className="camera-loading">
        <p>カメラデータを読み込み中...</p>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // エラー時の表示
  if (error) {
    return (
      <div className="camera-error">
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>再読み込み</button>
      </div>
    );
  }

  // カメラ詳細表示
  if (selectedCamera) {
    return (
      <div className="camera-detail-container">
        <div className="camera-detail-header">
          <h4>{selectedCamera.name}</h4>
          <button onClick={handleCloseDetail} className="back-button">戻る</button>
        </div>
        
        <div className="camera-detail-content">
          <div className="camera-live-feed">
            <img 
              src={selectedCamera.thumbnail} 
              alt={`${selectedCamera.name}のライブフィード`} 
              className={selectedCamera.status !== 'online' ? 'offline-feed' : ''}
            />
            {selectedCamera.status !== 'online' && (
              <div className="offline-overlay">
                <p>{getStatusText(selectedCamera.status)}</p>
              </div>
            )}
          </div>
          
          <div className="camera-info-panel">
            <div className="info-item">
              <span className="info-label">ステータス:</span>
              <span className={`info-value ${getStatusClass(selectedCamera.status)}`}>
                {getStatusText(selectedCamera.status)}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">設置場所:</span>
              <span className="info-value">{selectedCamera.location}</span>
            </div>
            <div className="info-item">
              <span className="info-label">解像度:</span>
              <span className="info-value">{selectedCamera.resolution}</span>
            </div>
            <div className="info-item">
              <span className="info-label">ストレージ使用量:</span>
              <span className="info-value">{selectedCamera.storage} GB</span>
            </div>
            <div className="info-item">
              <span className="info-label">最終更新:</span>
              <span className="info-value">{formatDateTime(selectedCamera.lastUpdate)}</span>
            </div>
          </div>
          
          <div className="camera-controls">
            <button className="control-button">録画再生</button>
            <button className="control-button">設定</button>
            <button className="control-button">アラート履歴</button>
            <button className="control-button">スナップショット</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="camera-container">
      <div className="camera-header">
        <h3>クラウドカメラ管理</h3>
        <div className="camera-controls-header">
          <button onClick={toggleViewMode} className="view-mode-button">
            {viewMode === 'grid' ? 'リスト表示' : 'グリッド表示'}
          </button>
          <p className="update-date">最終更新: {cameraData.summary.updateDate}</p>
        </div>
      </div>
      
      <div className="camera-summary">
        <div className="summary-card">
          <h4>カメラ総数</h4>
          <p className="summary-value">{cameraData.summary.totalCameras}</p>
        </div>
        <div className="summary-card">
          <h4>オンライン</h4>
          <p className="summary-value status-online">{cameraData.summary.onlineCount}</p>
        </div>
        <div className="summary-card">
          <h4>オフライン</h4>
          <p className="summary-value status-offline">{cameraData.summary.offlineCount}</p>
        </div>
        <div className="summary-card">
          <h4>警告</h4>
          <p className="summary-value status-warning">{cameraData.summary.warningCount}</p>
        </div>
      </div>
      
      <div className="storage-summary">
        <h4>ストレージ使用状況</h4>
        <div className="storage-bar-container">
          <div 
            className="storage-bar" 
            style={{width: `${(cameraData.summary.usedStorage / cameraData.summary.totalStorage) * 100}%`}}
          ></div>
        </div>
        <p className="storage-text">
          {cameraData.summary.usedStorage} GB / {cameraData.summary.totalStorage} GB 
          ({Math.round((cameraData.summary.usedStorage / cameraData.summary.totalStorage) * 100)}%)
        </p>
      </div>
      
      {viewMode === 'grid' ? (
        <div className="camera-grid">
          {cameraData.cameras.map((camera) => (
            <div 
              key={camera.id} 
              className={`camera-card ${getStatusClass(camera.status)}`}
              onClick={() => handleCameraClick(camera)}
            >
              <div className="camera-thumbnail">
                <img src={camera.thumbnail} alt={camera.name} />
                <span className={`status-badge ${getStatusClass(camera.status)}`}>
                  {getStatusText(camera.status)}
                </span>
              </div>
              <div className="camera-info">
                <h5>{camera.name}</h5>
                <p>{camera.location}</p>
                <p className="camera-update">更新: {formatDateTime(camera.lastUpdate)}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="camera-list">
          <table className="camera-table">
            <thead>
              <tr>
                <th>カメラ名</th>
                <th>設置場所</th>
                <th>ステータス</th>
                <th>解像度</th>
                <th>ストレージ</th>
                <th>最終更新</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {cameraData.cameras.map((camera) => (
                <tr key={camera.id} className={getStatusClass(camera.status)}>
                  <td>{camera.name}</td>
                  <td>{camera.location}</td>
                  <td>
                    <span className={`status-indicator ${getStatusClass(camera.status)}`}></span>
                    {getStatusText(camera.status)}
                  </td>
                  <td>{camera.resolution}</td>
                  <td>{camera.storage} GB</td>
                  <td>{formatDateTime(camera.lastUpdate)}</td>
                  <td>
                    <button 
                      onClick={() => handleCameraClick(camera)} 
                      className="view-button"
                    >
                      詳細
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      <div className="camera-footer">
        <p>※ このデータはモックデータです</p>
      </div>
    </div>
  );
};

export default CameraManagement;
